/**
 * Computes the visual bounds of an image (ignoring transparent pixels).
 * Returns the relative coordinates (0-1) of the non-transparent area.
 */
export async function computeVisualBounds(img: HTMLImageElement | HTMLCanvasElement): Promise<{ left: number, top: number, right: number, bottom: number }> {
    try {
        const canvas = document.createElement('canvas');
        const width = 'naturalWidth' in img ? img.naturalWidth : img.width;
        const height = 'naturalHeight' in img ? img.naturalHeight : img.height;

        const scanWidth = Math.min(width, 512);
        const scanHeight = Math.floor(height * (scanWidth / width));
        canvas.width = scanWidth;
        canvas.height = scanHeight;

        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Could not get canvas context');

        ctx.drawImage(img, 0, 0, scanWidth, scanHeight);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height).data;

        let minX = canvas.width, minY = canvas.height, maxX = 0, maxY = 0;
        let found = false;

        for (let y = 0; y < canvas.height; y++) {
            for (let x = 0; x < canvas.width; x++) {
                const alpha = imageData[((y * canvas.width + x) << 2) + 3];
                if (alpha > 10) { // Transparency threshold
                    if (x < minX) minX = x;
                    if (x > maxX) maxX = x;
                    if (y < minY) minY = y;
                    if (y > maxY) maxY = y;
                    found = true;
                }
            }
        }

        if (!found) {
            return { left: 0, top: 0, right: 1, bottom: 1 };
        }

        return {
            left: minX / canvas.width,
            top: minY / canvas.height,
            right: (maxX + 1) / canvas.width,
            bottom: (maxY + 1) / canvas.height
        };
    } catch {
        // Cross-origin or other error — return full bounds as safe fallback
        return { left: 0, top: 0, right: 1, bottom: 1 };
    }
}
